import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { AnalysisPage } from "./components/AnalysisPage";
import { DraftPage } from "./components/DraftPage";
import { HomePage } from "./components/HomePage";
import { LeaderboardPage } from "./components/LeaderboardPage";
import { NotFoundPage } from "./components/NotFoundPage";
import { ScoutPage } from "./components/ScoutPage";
import { SearchBar } from "./components/SearchBar";
import { SummonerPage } from "./components/SummonerPage";
import { TierListPage } from "./components/TierListPage";
import "./index.css";
import LogoSvg from "./logo.svg";

const TOOLS = [
	{ to: "/scout", label: "Scout" },
	{ to: "/draft", label: "Draft" },
	{ to: "/analysis", label: "Split review" },
	{ to: "/leaderboard", label: "Leaderboards" },
];

function ToolsMenu({ className }: { className?: string }) {
	const { pathname } = useLocation();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const active = TOOLS.some((t) => pathname.startsWith(t.to));

	// close on navigation, outside click, or Escape
	useEffect(() => setOpen(false), [pathname]);
	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
		const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	return (
		<div ref={ref} className={cn("relative ml-auto shrink-0", className)}>
			<button
				type="button"
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((o) => !o)}
				className={cn("flex h-10 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-[13px] font-semibold transition-colors hover:bg-raised hover:text-ink", open && "bg-raised", active || open ? "text-ink" : "text-ink-2")}
			>
				Tools
				<ChevronDown aria-hidden="true" className={cn("h-3 w-3 text-ink-3 transition-transform", open && "rotate-180")} />
			</button>
			{open && (
				<div role="menu" className="absolute right-0 top-[46px] z-50 min-w-[160px] rounded-[10px] border border-line-2 bg-surface p-1.5 shadow-[0_18px_40px_rgba(0,0,0,.5)] animate-reveal">
					{TOOLS.map((t) => (
						<Link
							key={t.to}
							to={t.to}
							role="menuitem"
							className="block rounded-lg px-2.5 py-2 text-[13px] font-semibold text-ink-2 hover:bg-[#232a37] hover:text-ink"
						>
							{t.label}
						</Link>
					))}
				</div>
			)}
		</div>
	);
}

export function App() {
	const { pathname } = useLocation();
	const onHome = pathname === "/";
	const onDraft = pathname === "/draft"; // the draft board uses the full page width

	return (
		<div className="min-h-screen w-full flex flex-col">
			<a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-signal focus:px-3 focus:py-2 focus:text-on-signal">
				Skip to content
			</a>
			<header className="sticky top-0 z-50 border-b border-line bg-page/90 backdrop-blur-md supports-[padding:env(safe-area-inset-top)]:pt-[env(safe-area-inset-top)]">
				<div className="mx-auto flex w-full max-w-[1180px] items-center gap-4 px-4 py-2.5">
					<Link to="/" aria-label="RiftWatch home" className="flex shrink-0 items-center gap-2.5">
						<img src={LogoSvg} alt="" className="h-[30px] w-[30px]" />
						<span className={onHome ? "text-[15px] font-extrabold tracking-[.04em] [font-stretch:125%]" : "hidden sm:inline text-[15px] font-extrabold tracking-[.04em] [font-stretch:125%]"}>
							RIFTWATCH
						</span>
					</Link>
					<Link to="/tierlist" className="flex h-10 shrink-0 items-center rounded-lg px-3 text-[13px] font-semibold text-ink-2 transition-colors hover:bg-raised hover:text-ink">
						Tier list
					</Link>
					<ToolsMenu />
					{/* the home page has its own big search box */}
					{!onHome && (
						<div className="min-w-0 flex-1 max-w-[520px]">
							<SearchBar size="sm" />
						</div>
					)}
				</div>
			</header>

			<main id="main" className={cn("mx-auto w-full flex-1 px-4 pb-12", onDraft ? "max-w-none" : "max-w-[1180px]")}>
				<Routes>
					<Route path="/" element={<HomePage />} />
					<Route path="/summoners/:region/:nameTag" element={<SummonerPage />} />
					<Route path="/scout/:id?" element={<ScoutPage />} />
					<Route path="/draft" element={<DraftPage />} />
					<Route path="/analysis" element={<AnalysisPage />} />
					<Route path="/tierlist" element={<TierListPage />} />
					<Route path="/leaderboard/:slug?" element={<LeaderboardPage />} />
					<Route path="*" element={<NotFoundPage />} />
				</Routes>
			</main>

			<footer className="mt-auto border-t border-line">
				<div className="mx-auto flex w-full max-w-[1180px] flex-wrap items-start gap-x-6 gap-y-3 px-4 pt-5 pb-7">
					<span className="text-xs font-extrabold tracking-[.04em] text-ink-2 [font-stretch:125%]">RIFTWATCH</span>
					<p className="m-0 max-w-[760px] flex-[1_1_320px] text-xs leading-relaxed text-ink-3">
						RiftWatch isn&apos;t endorsed by Riot Games and doesn&apos;t reflect the views or opinions of Riot Games or anyone officially
						involved in producing or managing Riot Games properties. Riot Games, and all associated properties are trademarks or
						registered trademarks of Riot Games, Inc.
					</p>
				</div>
			</footer>
		</div>
	);
}

export default App;
